import Markdown, { Components } from "react-markdown";
import RemarkGfm from "remark-gfm";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  styled,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { CSSProperties, ReactNode, useMemo } from "react";
import { Link } from "react-router-dom";
import { ChatCodeBlock } from "./Code";
import { ChatChart, ChatChartPlaceholder } from "./ChatChart";
import { ToolCallEntry } from "../ChatBox/types";
import {
  CitationResults,
  citationHref,
  citationIdFromHref,
  replaceCitationMarkers,
} from "./citationMarkers";

function classes(...classNames: (string | undefined)[]) {
  return classNames.filter(Boolean).join(" ");
}

export const MarkdownContent = styled("div")(({ theme }) => ({
  fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`,
  lineHeight: 1.8,
  color: theme.palette.mode === "dark" ? "#f0f0f0" : "#1a1a1a",
  backgroundColor: theme.palette.mode === "dark" ? "#1a1a1a" : "#ffffff",

  "& .markdown-table": {
    borderCollapse: "collapse",
    width: "100%",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 2px 8px rgba(255,255,255,0.1)"
        : "0 2px 8px rgba(0,0,0,0.1)",
    "@media (max-width: 768px)": {
      display: "block",
      overflowX: "auto",
    },

    "& th": {
      backgroundColor: theme.palette.mode === "dark" ? "#2d2d2d" : "#eef2f7",
      fontWeight: 600,
      color: theme.palette.mode === "dark" ? "#f0f0f0" : "#000",
      padding: "16px 20px",
      userSelect: "all", // Make text selectable
    },

    "& td": {
      userSelect: "all", // Make text selectable
    },

    "& tr:nth-of-type(even)": {
      backgroundColor: theme.palette.mode === "dark" ? "#242424" : "#f8fafc",
    },
  },

  "& a": {
    color: theme.palette.mode === "dark" ? "#7ab8ff" : "#0066cc",
    "&:hover": {
      color: theme.palette.mode === "dark" ? "#99ccff" : "#0052a3",
    },
  },
}));

const ExportableTable = ({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) => {
  return (
    <div>
      <Table
        size="small"
        className={classes(className, "markdown-table")}
        style={style}
      >
        {children}
      </Table>

      <button
        style={{
          marginTop: "4px",
          background: "#f0f0f0",
          border: "1px solid #ccc",
          borderRadius: "3px",
          padding: "2px 5px",
          fontSize: "12px",
          cursor: "pointer",
          // Keeps the label out of the clipboard when the whole reply is selected.
          userSelect: "none",
        }}
        onClick={e => {
          // Find the table element
          const table = e.currentTarget.parentElement?.querySelector("table");
          if (!table) return;

          // Extract table data
          const rows = Array.from(table.querySelectorAll("tr"));
          const data = rows
            .map(row => {
              const cells = Array.from(row.querySelectorAll("th, td"));
              return cells.map(cell => cell.textContent).join("\t");
            })
            .join("\n");

          // Copy to clipboard
          navigator.clipboard.writeText(data);

          // Provide visual feedback
          const button = e.currentTarget;
          const originalText = button.textContent;
          button.textContent = "Copied!";
          setTimeout(() => {
            button.textContent = originalText;
          }, 2000);
        }}
      >
        Copy Table
      </button>
    </div>
  );
};

const remarkPlugins = [RemarkGfm];

/**
 * A markdown link target cannot contain spaces, so `[f](atlas-file:My File.xlsx)`
 * renders as plain text. Percent-encoding the name keeps it a link, for new
 * answers and ones already saved; the `a` override decodes it to look it up.
 */
const ATLAS_LINK_WITH_SPACE_RE = /\]\(atlas-file:([^()\n]*\s[^()\n]*)\)/g;
const withAtlasLinksEncoded = (md: string) =>
  md.replace(ATLAS_LINK_WITH_SPACE_RE, (_, name: string) => `](atlas-file:${encodeURIComponent(name)})`);

/** decodeURIComponent, but a stray `%` in a name leaves it as written instead of throwing mid-render. */
function decodedName(name: string): string {
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
}

/**
 * react-markdown's default URL sanitizer strips unknown protocols, which
 * deletes the `atlas-file:` placeholder href before our `a` override sees it
 * — the link then renders without a href and clicking opens the current
 * page in a new tab. Pass our own urlTransform that lets `atlas-file:`
 * through, blocks the genuinely dangerous schemes, and otherwise leaves
 * URLs alone.
 */
function urlTransform(url: string): string {
  if (url.startsWith("atlas-file:")) return url;
  if (/^(javascript|vbscript|data):/i.test(url)) return "";
  return url;
}

const CHART_MARKER_RE = /\{\{chart:([^}]+)\}\}/g;
/** Matches a partial trailing marker during streaming, e.g. `{{chart:abc` or `{{citation:to` */
const PARTIAL_MARKER_RE = /\{\{(?:chart|citation)[^}]*$/;

/**
 * Inline citation affordance: a small clickable "Source" marker placed
 * immediately after a cited figure. Clicking opens the document reader at the
 * cited excerpt; hovering shows the citation description.
 */
function CitationMarker({ entry }: { entry?: ToolCallEntry }) {
  const results: CitationResults | undefined =
    entry?.results && typeof entry.results === "object"
      ? entry.results
      : undefined;
  // Citation tool still running or marker has no matching entry — render
  // nothing rather than a dead marker.
  if (!results) return null;

  const href = citationHref(results);
  const description = results.citationDescription || "View source";

  const marker = (
    <sup
      data-cy="chat-citation-marker"
      style={{
        fontSize: "0.7em",
        fontWeight: 600,
        lineHeight: 0,
        padding: "0 2px",
        whiteSpace: "nowrap",
        cursor: href ? "pointer" : "default",
      }}
    >
      Source
    </sup>
  );

  if (!href) {
    // No destination could be built (e.g. inlineDocument, or a citation
    // missing its source id) — show the marker (with tooltip) without a link.
    return (
      <Tooltip title={description}>
        <span style={{ color: "gray" }}>{marker}</span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={description}>
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        style={{ textDecoration: "none" }}
      >
        {marker}
      </a>
    </Tooltip>
  );
}

/**
 * Split markdown text on `{{chart:<id>}}` markers.
 * Returns alternating [text, chartId, text, chartId, ...] segments.
 */
function splitOnChartMarkers(md: string): Array<{ type: "text"; value: string } | { type: "chart"; toolId: string }> {
  const segments: Array<{ type: "text"; value: string } | { type: "chart"; toolId: string }> = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // Reset lastIndex to ensure fresh iteration
  CHART_MARKER_RE.lastIndex = 0;
  while ((match = CHART_MARKER_RE.exec(md)) !== null) {
    const textBefore = md.slice(lastIndex, match.index);
    if (textBefore) {
      segments.push({ type: "text", value: textBefore });
    }
    segments.push({ type: "chart", toolId: match[1] });
    lastIndex = match.index + match[0].length;
  }

  const remaining = md.slice(lastIndex);
  if (remaining) {
    segments.push({ type: "text", value: remaining });
  }

  return segments;
}

export function ChatMarkdown({
  children,
  isStreaming,
  tools,
}: {
  children?: string | null;
  isStreaming?: boolean;
  tools?: ToolCallEntry[];
}) {
  // Index CitationTool entries by the tool_use id the model embeds in its
  // {{citation:<id>}} markers, so each marker resolves to the call it names.
  // Matching by position instead would swap sources between figures whenever
  // the model presents its findings in a different order than it made the
  // CitationTool calls in — which is the common case for parallel calls.
  const citationEntryByToolCallId = useMemo(() => {
    const map = new Map<string, ToolCallEntry>();
    if (!tools) return map;
    for (const t of tools) {
      if (t.tool === "CitationTool" && t.toolCallId) map.set(t.toolCallId, t);
    }
    return map;
  }, [tools]);

  // Build a filename → downloadUrl map from all AtlasTool entries' artifacts.
  // The model writes `[name](atlas-file:name)` links in its reply; the `a`
  // override below swaps the placeholder href for the real presigned URL so
  // the link is clickable without exposing the long URL to the model (where
  // it could be corrupted during streaming or paraphrasing).
  const atlasFileUrlByName = useMemo(() => {
    const map = new Map<string, string>();
    if (!tools) return map;
    for (const t of tools) {
      if (t.tool !== "AtlasTool") continue;
      const files = t.results?.outputFiles as
        | { name: string; downloadUrl: string }[]
        | undefined;
      if (!files) continue;
      for (const f of files) {
        if (f.name && f.downloadUrl) map.set(f.name, f.downloadUrl);
      }
    }
    return map;
  }, [tools]);

  const components: Components = useMemo(
    () => ({
      a({ node, ...props }) {
        const href = props.href || "";
        // atlas-file:<filename> placeholder → resolve to the real download
        // URL from the AtlasTool artifact's outputFiles. If we can't find a
        // match (tool result not yet available, or wrong filename), keep the
        // link disabled so the user doesn't get a broken navigation.
        const atlasMatch = href.match(/^atlas-file:(.+)$/);
        if (atlasMatch) {
          const resolved =
            atlasFileUrlByName.get(atlasMatch[1]) ?? atlasFileUrlByName.get(decodedName(atlasMatch[1]));
          if (resolved) {
            return (
              <a href={resolved} target="_blank" rel="noreferrer">
                {props.children}
              </a>
            );
          }
          return <span>{props.children}</span>;
        }
        // Synthetic hrefs produced by replaceCitationMarkers — render the
        // clickable "Source" marker instead of a link. Both #cite-<id> (inline)
        // and #citeref-<id> (legacy paragraph-end) name the CitationTool call
        // that backs this marker by its tool_use id.
        const citationId = citationIdFromHref(href);
        if (citationId !== undefined) {
          return (
            <CitationMarker entry={citationEntryByToolCallId.get(citationId)} />
          );
        }
        // Use React Router for same-origin links
        try {
          const url = new URL(href, window.location.origin);
          if (url.origin === window.location.origin) {
            const to = url.pathname + url.search + url.hash;
            return (
              <Link
                to={to}
                onClick={() => {
                  // Blur active element to trigger sidebar collapse from expanded mode
                  (document.activeElement as HTMLElement)?.blur();
                }}
              >
                {props.children}
              </Link>
            );
          }
        } catch {
          // Invalid URL — fall through to default
        }
        return <a {...props} target="_blank" rel="noreferrer" />;
      },
      table({ children, style, className }) {
        return (
          <ExportableTable
            className={className}
            style={style as CSSProperties}
          >
            {children}
          </ExportableTable>
        );
      },
      thead({ children, style }) {
        return (
          <TableHead style={style as CSSProperties}>{children}</TableHead>
        );
      },
      tbody({ children, style }) {
        return (
          <TableBody style={style as CSSProperties}>{children}</TableBody>
        );
      },
      tr({ children, style }) {
        return <TableRow style={style as CSSProperties}>{children}</TableRow>;
      },
      td({ children, style }) {
        return (
          <TableCell
            style={{ ...(style as CSSProperties), padding: "8px 16px" }}
          >
            {children}
          </TableCell>
        );
      },
      th({ children, style }) {
        return (
          <TableCell
            style={{ ...(style as CSSProperties), padding: "8px 16px" }}
          >
            {children}
          </TableCell>
        );
      },
      li({ className, node, ...props }) {
        return <li {...props} className={classes(className, "markdown-li")} />;
      },
      ol({ className, node, ...props }) {
        return <ol {...props} className={classes(className, "markdown-ol")} />;
      },
      ul({ className, node, ...props }) {
        return <ul {...props} className={classes(className, "markdown-ul")} />;
      },
      // Backwards compatibility: old persisted conversations may contain ```chart blocks
      code({ className, node, children: codeChildren, ...props }) {
        if (className === "language-chart") {
          const rawJson = String(
            Array.isArray(codeChildren)
              ? codeChildren.join("")
              : (codeChildren ?? "")
          ).trim();
          return <ChatChart rawJson={rawJson} isStreaming={isStreaming} />;
        }
        // Inline code (no language class) renders as styled <code>, not a full block
        if (!className) {
          return (
            <code
              style={{
                backgroundColor: "rgba(0,0,0,0.06)",
                borderRadius: 3,
                padding: "2px 5px",
                fontSize: "0.88em",
                fontFamily: 'Consolas, Monaco, "Andale Mono", monospace',
              }}
              {...props}
            >
              {codeChildren}
            </code>
          );
        }
        return <ChatCodeBlock {...props}>{codeChildren}</ChatCodeBlock>;
      },
      pre({ children }) {
        return <>{children}</>;
      },
    }),
    [isStreaming, citationEntryByToolCallId, atlasFileUrlByName]
  );

  // Collect ChartTool entries in order for positional matching: the Nth
  // {{chart:...}} marker renders the Nth ChartTool entry.
  //
  // Chart markers name their call by tool_use id just like citation markers do,
  // so this is open to the same mismatch, but it is deliberately still
  // positional: unlike citations, ChartTool entries ARE replayed from persisted
  // conversations (see ConversationHistoryTab), and those predate toolCallId —
  // switching to id matching would blank out every chart already in history.
  // Moving charts over needs a fallback for that older data.
  const chartToolEntries = useMemo(() => {
    if (!tools) return [];
    return tools.filter(t => t.tool === "ChartTool");
  }, [tools]);

  const md = children ?? "";

  // During streaming, trim partial trailing markers (e.g. `{{chart:abc` without closing `}}`)
  const trimmedMd = isStreaming ? md.replace(PARTIAL_MARKER_RE, "") : md;
  // Citation markers become inline links before chart splitting so they stay
  // within their paragraph's markdown.
  const cleanedMd = useMemo(
    () => replaceCitationMarkers(withAtlasLinksEncoded(trimmedMd)),
    [trimmedMd]
  );

  const segments = useMemo(
    () => splitOnChartMarkers(cleanedMd),
    [cleanedMd]
  );

  const hasChartMarkers = segments.some(s => s.type === "chart");

  // Fast path: no chart markers — render markdown directly (most common case)
  if (!hasChartMarkers) {
    return (
      <MarkdownContent className="chat-markdown">
        <Markdown
          remarkPlugins={remarkPlugins}
          components={components}
          urlTransform={urlTransform}
        >
          {cleanedMd}
        </Markdown>
      </MarkdownContent>
    );
  }

  // Slow path: interleave markdown text segments with chart components
  let chartIndex = 0;
  return (
    <MarkdownContent className="chat-markdown">
      {segments.map((segment, i) => {
        if (segment.type === "text") {
          return (
            <Markdown
              key={i}
              remarkPlugins={remarkPlugins}
              components={components}
              urlTransform={urlTransform}
            >
              {segment.value}
            </Markdown>
          );
        }

        // Chart marker — resolve by position from ChartTool entries
        const toolEntry = chartToolEntries[chartIndex++];
        const results = toolEntry?.results;
        const hasResults =
          results && typeof results === "object" && Object.keys(results).length > 0;

        if (hasResults) {
          return <ChatChart key={i} spec={results} />;
        }

        // Tool still in progress or not found — show placeholder
        return (
          <ChatChartPlaceholder key={i}>
            <CircularProgress size={20} color="inherit" />
            Generating chart...
          </ChatChartPlaceholder>
        );
      })}
    </MarkdownContent>
  );
}
